import React from "react";
import { Question } from "../survey-types";
import MultipleChoiceEditor from "./MultipleChoiceEditor";

interface SingleQuestionEditorProps {
  question: Question;
  onChange: (q: Question) => void;
  onDelete: () => void;
}

const SingleQuestionEditor: React.FC<SingleQuestionEditorProps> = ({
  question,
  onChange,
  onDelete,
}) => {
  const handleQuestionChange = (key: keyof Question, value: any) => {
    const q = { ...question } as any;
    q[key] = value;
    onChange(q);
  };

  return (
    <div className="flex">
      <div className="flex-1">
        <input
          type="text"
          placeholder="Your question here"
          className="bg-slate-600 text-white text-xl block w-full max-w-[800px]"
          value={question.text}
          onChange={(e) => handleQuestionChange("text", e.target.value)}
        />
        <input
          type="text"
          placeholder="Description (Optional): use it as a context hint for the respondent to better interpret the question."
          className="bg-slate-600 text-white text-sm italic mt-8 block w-full max-w-[800px]"
          value={question.description || ""}
          onChange={(e) => handleQuestionChange("description", e.target.value)}
        />

        {question.type == "text" && (
          <label className="block mt-8">
            Max answer length: &nbsp;
            <input
              type="number"
              placeholder="Optional"
              onChange={(e) =>
                handleQuestionChange("maxAnswerLength", Number(e.target.value))
              }
              className="bg-slate-600 text-white w-[200px]"
            />
          </label>
        )}

        {(question.type == "multiple-choice" || question.type == "ranking") && (
          <MultipleChoiceEditor
            choices={question.choices}
            onChange={(opts) => handleQuestionChange("choices", opts)}
          />
        )}
        {question.type == "multiple-choice" && (
          <label className="block mt-8">
            <input
              type="checkbox"
              className="bg-slate-600"
              checked={question.multipleSelection || false}
              onChange={(e) =>
                handleQuestionChange("multipleSelection", e.target.checked)
              }
            />
            &nbsp; Allow multiple answers
          </label>
        )}

        {question.type == "number" && (
          <>
            <label className="block mt-8">
              Min: &nbsp;
              <input
                type="number"
                placeholder="Optional"
                onChange={(e) =>
                  handleQuestionChange("min", Number(e.target.value))
                }
                className="bg-slate-600 text-white w-[200px]"
              />
            </label>
            <label className="block mt-8">
              Max: &nbsp;
              <input
                type="number"
                placeholder="Optional"
                onChange={(e) =>
                  handleQuestionChange("max", Number(e.target.value))
                }
                className="bg-slate-600 text-white w-[200px]"
              />
            </label>
            <label className="block mt-8">
              Must be integer: &nbsp;
              <input
                type="checkbox"
                className="bg-slate-600"
                checked={question.integer || false}
                onChange={(e) =>
                  handleQuestionChange("mustBeInteger", e.target.checked)
                }
              />
              &nbsp; Integer
            </label>
          </>
        )}

        {question.type == "opinion-scale" && (
          <div className="mt-8">
            <div>
              <label>
                From:{" "}
                <input
                  type="number"
                  placeholder="Minimum value (number)"
                  onChange={(e) =>
                    handleQuestionChange("min", Number(e.target.value))
                  }
                  className="bg-slate-600 text-white w-[300px]"
                />
              </label>
              <label>
                &nbsp; To:&nbsp;
                <input
                  type="number"
                  placeholder="Maximum value (number)"
                  onChange={(e) =>
                    handleQuestionChange("max", Number(e.target.value))
                  }
                  className="bg-slate-600 text-white w-[300px]"
                />
              </label>
            </div>
            <div className="mt-4">
              <label>
                From:{" "}
                <input
                  type="text"
                  placeholder="Minimum label (text, optional)"
                  onChange={(e) =>
                    handleQuestionChange("minLabel", e.target.value)
                  }
                  className="bg-slate-600 text-white w-[300px]"
                />
              </label>
              <label>
                &nbsp; To:&nbsp;
                <input
                  type="text"
                  placeholder="Maximum label (text, optional)"
                  onChange={(e) =>
                    handleQuestionChange("maxLabel", e.target.value)
                  }
                  className="bg-slate-600 text-white w-[300px]"
                />
              </label>
            </div>
          </div>
        )}
        {question.type == "matrix" && (
          <div className="text-red-500 mt-16 mb-16">
            Warning: the Matrix type is probably important but not implemented
            yet.
          </div>
        )}
      </div>
      <div className="w-[300px] pl-4 flex-none">
        <label className="block mb-8">
          Question type: &nbsp;
          <select
            id="questionType"
            value={question.type}
            onChange={(e) => handleQuestionChange("type", e.target.value)}
            className="border border-gray-300 p-2 bg-slate-600 text-white"
          >
            <option value="text">Text</option>
            <option value="yes-no">Yes/No</option>
            <option value="multiple-choice">Multiple Choice</option>
            <option value="number">Number</option>
            <option value="opinion-scale">Opinion Scale</option>
            <option value="ranking">Ranking</option>
            <option value="matrix">Matrix</option>
          </select>
        </label>
        {/* <label className="block mb-8">
          <input
            type="checkbox"
            className="bg-slate-600"
            checked={question.required}
            onChange={(e) => handleQuestionChange("required", e.target.checked)}
          />
          &nbsp; Required
        </label> */}
        <button className="underline" onClick={onDelete}>
          Delete Question
        </button>
      </div>
    </div>
  );
};

export default SingleQuestionEditor;
